import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Clock, Send, MessageCircle } from 'lucide-react';
import { useForm } from 'react-hook-form';
import InteractiveMap from '../components/InteractiveMap.tsx';

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, #d4af37 0%, #b8941f 100%);
  color: white;
  padding: 3rem 0;
  text-align: center;
  border-radius: 0 0 20px 20px;
  margin-bottom: 3rem;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 1rem;
  font-weight: bold;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  opacity: 0.9;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  margin-bottom: 3rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const ContactInfo = styled.section`
  background: white;
  border-radius: 15px;
  padding: 2rem;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
`;

const ContactForm = styled(motion.form)`
  background: white;
  border-radius: 15px;
  padding: 2rem;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
`;

const SectionTitle = styled.h2`
  color: #2c3e50;
  font-size: 1.8rem;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 10px;
  transition: all 0.3s ease;
  
  &:hover {
    background: #e9ecef;
    transform: translateX(5px);
  }
  
  .icon {
    color: #d4af37;
    font-size: 1.5rem;
  }
  
  .content {
    h4 {
      color: #2c3e50;
      margin: 0 0 0.25rem 0;
      font-size: 1.1rem;
    }
    
    p {
      color: #7f8c8d;
      margin: 0;
      font-size: 0.9rem;
    }
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  color: #2c3e50;
  font-weight: 500;
  margin-bottom: 0.5rem;
  display: block;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #d4af37;
  }
  
  &.error {
    border-color: #e74c3c;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 1rem;
  resize: vertical;
  min-height: 120px;
  transition: border-color 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #d4af37;
  }
  
  &.error {
    border-color: #e74c3c;
  }
`;

const ErrorMessage = styled.span`
  color: #e74c3c;
  font-size: 0.9rem;
  margin-top: 0.25rem;
  display: block;
`;

const SubmitButton = styled.button`
  background: #d4af37;
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    background: #b8941f;
    transform: translateY(-2px);
  }
  
  &:disabled {
    background: #95a5a6;
    cursor: not-allowed;
    transform: none;
  }
`;

const SuccessMessage = styled(motion.div)`
  background: #27ae60;
  color: white;
  padding: 1.5rem;
  border-radius: 10px;
  text-align: center;
  margin-bottom: 2rem;
  
  h3 {
    margin-bottom: 0.5rem;
  }
  
  p {
    opacity: 0.9;
  }
`;

const OpeningHours = styled.div`
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 10px;
  margin-top: 2rem;
  
  h4 {
    color: #2c3e50;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  
  .hours-grid {
    display: grid;
    gap: 0.5rem;
  }
  
  .hour-row {
    display: flex;
    justify-content: space-between;
    padding: 0.5rem 0;
    border-bottom: 1px solid #e9ecef;
    
    &:last-child {
      border-bottom: none;
    }
    
    .day {
      font-weight: 500;
      color: #2c3e50;
    }
    
    .time {
      color: #7f8c8d;
    }
  }
`;

const FAQSection = styled.section`
  background: white;
  border-radius: 15px;
  padding: 2rem;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
  margin-bottom: 3rem;
`;

const FAQItem = styled.div`
  margin-bottom: 1.5rem;
  
  h4 {
    color: #2c3e50;
    margin-bottom: 0.5rem;
    font-size: 1.1rem;
  }
  
  p {
    color: #7f8c8d;
    line-height: 1.6;
  }
`;

interface ContactFormData {
  name: string;
  email: string;
  subject: string;
  message: string;
}

const ContactPage: React.FC = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset
  } = useForm<ContactFormData>();

  const onSubmit = async (data: ContactFormData) => {
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    console.log('Contact form data:', data);
    setIsSubmitted(true);
    reset();
  };

  if (isSubmitted) {
    return (
      <PageContainer>
        <SuccessMessage
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h3>🎉 Bericht Verzonden!</h3>
          <p>
            Bedankt voor je bericht. We nemen zo snel mogelijk contact met je op.
          </p>
        </SuccessMessage>
        
        <ContactForm
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <SubmitButton onClick={() => setIsSubmitted(false)}>
            <Send size={20} />
            Nieuw Bericht
          </SubmitButton>
        </ContactForm>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <HeroSection>
        <HeroTitle>Contact</HeroTitle>
        <HeroSubtitle>
          Neem contact met ons op voor vragen, reserveringen of feedback
        </HeroSubtitle>
      </HeroSection>

      <ContentGrid>
        <ContactInfo>
          <SectionTitle>
            <MessageCircle size={24} />
            Contact Informatie
          </SectionTitle>

          <ContactItem>
            <div className="icon">
              <Mail size={24} />
            </div>
            <div className="content">
              <h4>E-mail</h4>
              <p>info@poulepoulette.com</p>
            </div>
          </ContactItem>

          <ContactItem>
            <div className="icon">
              <Phone size={24} />
            </div>
            <div className="content">
              <h4>Telefoon</h4>
              <p>+32 2 123 45 67</p>
            </div>
          </ContactItem>

          <ContactItem>
            <div className="icon">
              <MapPin size={24} />
            </div>
            <div className="content">
              <h4>Hoofdkantoor</h4>
              <p>Grote Markt 15, 1000 Brussel</p>
            </div>
          </ContactItem>

          <OpeningHours>
            <h4>
              <Clock size={20} />
              Openingstijden
            </h4>
            <div className="hours-grid">
              <div className="hour-row">
                <span className="day">Maandag - Vrijdag</span>
                <span className="time">11:00 - 22:00</span>
              </div>
              <div className="hour-row">
                <span className="day">Zaterdag</span>
                <span className="time">12:00 - 23:00</span>
              </div>
              <div className="hour-row">
                <span className="day">Zondag</span>
                <span className="time">12:00 - 21:00</span>
              </div>
            </div>
          </OpeningHours>
        </ContactInfo>

        <ContactForm
          onSubmit={handleSubmit(onSubmit)}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <SectionTitle>
            <Send size={24} />
            Stuur ons een Bericht
          </SectionTitle>

          <FormGroup>
            <Label>Naam *</Label>
            <Input
              {...register('name', { required: 'Naam is verplicht' })}
              className={errors.name ? 'error' : ''}
              placeholder="Jouw naam"
            />
            {errors.name && <ErrorMessage>{errors.name.message}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label>E-mail *</Label>
            <Input
              type="email"
              {...register('email', { 
                required: 'E-mail is verplicht',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Ongeldig e-mailadres'
                }
              })}
              className={errors.email ? 'error' : ''}
              placeholder="jouw@email.com"
            />
            {errors.email && <ErrorMessage>{errors.email.message}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label>Onderwerp *</Label>
            <Input
              {...register('subject', { required: 'Onderwerp is verplicht' })}
              className={errors.subject ? 'error' : ''}
              placeholder="Waarover gaat je bericht?"
            />
            {errors.subject && <ErrorMessage>{errors.subject.message}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label>Bericht *</Label>
            <TextArea
              {...register('message', { required: 'Bericht is verplicht' })}
              className={errors.message ? 'error' : ''}
              placeholder="Schrijf hier je bericht..."
            />
            {errors.message && <ErrorMessage>{errors.message.message}</ErrorMessage>}
          </FormGroup>

          <SubmitButton type="submit" disabled={isSubmitting}>
            <Send size={20} />
            {isSubmitting ? 'Verzenden...' : 'Bericht Versturen'}
          </SubmitButton>
        </ContactForm>
      </ContentGrid>

      <FAQSection>
        <SectionTitle>Veelgestelde Vragen</SectionTitle>
        
        <FAQItem>
          <h4>Hoe kan ik een reservering maken?</h4>
          <p>
            Je kunt een reservering maken via onze website op de reserveringspagina, 
            of door ons te bellen op +32 2 123 45 67.
          </p>
        </FAQItem>

        <FAQItem>
          <h4>Kan ik takeaway bestellen?</h4>
          <p>
            Ja, je kunt online takeaway bestellen via onze website. 
            Je bestelling is binnen 15-20 minuten klaar voor ophaal.
          </p>
        </FAQItem>

        <FAQItem>
          <h4>Zijn er vegetarische opties beschikbaar?</h4>
          <p>
            Ja, we hebben verschillende vegetarische gerechten op ons menu, 
            waaronder onze populaire Veggie Poulette.
          </p>
        </FAQItem>

        <FAQItem>
          <h4>Wat zijn de openingstijden?</h4>
          <p>
            We zijn open van maandag tot vrijdag van 11:00 tot 22:00, 
            zaterdag van 12:00 tot 23:00, en zondag van 12:00 tot 21:00.
          </p>
        </FAQItem>

        <FAQItem>
          <h4>Kan ik een feestje of evenement organiseren?</h4>
          <p>
            Ja, we kunnen speciale arrangementen maken voor groepen en evenementen. 
            Neem contact met ons op voor meer informatie.
          </p>
        </FAQItem>
      </FAQSection>

      <section>
        <SectionTitle>Onze Locaties</SectionTitle>
        <InteractiveMap />
      </section>
    </PageContainer>
  );
};

export default ContactPage;
